import * as React from 'react';
import { observer } from 'mobx-react';

import ContentModal from 'shared/newComponents/modals/ContentModal';
import MultiLineCroppedContent from 'shared/newComponents/MultiLineCroppedContent';
import Typography from 'shared/newComponents/Typography';
import { ModalSize } from 'shared/newEntities/components/modals';
import type { NewsFeedItem } from 'shared/newEntities/newsFeed';
import {
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { useUiStore } from 'stores/index';
import { useNewsStore } from 'stores/newsStore';

import NewsModalCarousel from './NewsModalCarousel';
import styles from './NewsModalContent.modules.scss';
import { getNewsModalSubtitle } from './utils';

const NewsModalContent: React.FC = () => {
  const store = useNewsStore();
  const uiStore = useUiStore();
  const openedAt = React.useRef(new Date()).current;
  const [activeNews, setActiveNews] = React.useState<NewsFeedItem | null>(
    () => store?.items[0] || null
  );

  React.useEffect(() => {
    if (activeNews && store?.items.some((item) => item.id === activeNews.id)) {
      return;
    }

    setActiveNews(store?.items[0] || null);
  }, [activeNews, store?.items]);

  if (!store || !store.hasNews || !activeNews) {
    return null;
  }

  const title = (
    <MultiLineCroppedContent maxLines={3} popupContent={activeNews.title}>
      <Typography
        fontType={TypographyType.title}
        fontSize={uiStore.isMobile ? TypographySize.xs : TypographySize.s}
        fontWeight={TypographyWeight.heavy}
      >
        {activeNews.title}
      </Typography>
    </MultiLineCroppedContent>
  );

  return (
    <ContentModal
      className={styles['news-modal-content__modal']}
      opened
      fixHeight={false}
      onClose={store.markViewed}
      contentClassName={styles['news-modal-content__modal-main']}
      size={ModalSize.l}
      subTitle={getNewsModalSubtitle(activeNews, openedAt)}
      title={title}
    >
      <NewsModalCarousel<NewsFeedItem>
        activeItemId={activeNews.id}
        isCompactNavigation={uiStore.isDeskTopSSize}
        items={store.items}
        onActiveNewsChange={setActiveNews}
      />
    </ContentModal>
  );
};

export default observer(NewsModalContent);
